import React, { useState, useEffect, useCallback } from 'react';
import { pricingAPI } from '../../lib/api';
import { formatDateTime, formatCurrency, apiErrorMessage } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { DollarSign, Plus, Edit, Trash2, History, Clock } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'lab_test', label: 'Lab Test' },
  { value: 'imaging', label: 'Imaging' },
  { value: 'procedure', label: 'Procedure' },
  { value: 'medicine', label: 'Medicine' },
];

const PricingMaster = () => {
  const { user, hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [formData, setFormData] = useState({
    category: '',
    item_name: '',
    item_code: '',
    price: '',
    description: '',
  });

  const canManage = hasRole(['super_admin', 'admin', 'receptionist']);

  const fetchData = useCallback(async () => {
    try {
      const category = categoryFilter === 'all' ? null : categoryFilter;
      const [itemsRes, logRes] = await Promise.all([
        pricingAPI.getAll(category),
        pricingAPI.getAuditLog(),
      ]);
      setItems(itemsRes.data);
      setAuditLog(logRes.data);
    } catch (error) {
      toast.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);


  useEffect(() => {
    fetchData();
  }, [categoryFilter, fetchData]);
  const openAddDialog = () => {
    setEditMode(false);
    setSelectedItem(null);
    setFormData({
      category: '',
      item_name: '',
      item_code: '',
      price: '',
      description: '',
    });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditMode(true);
    setSelectedItem(item);
    setFormData({
      category: item.category,
      item_name: item.item_name,
      item_code: item.item_code,
      price: item.price.toString(),
      description: item.description || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
      };

      if (editMode) {
        await pricingAPI.update(selectedItem.id, data);
        toast.success('Pricing item updated successfully');
      } else {
        await pricingAPI.create(data);
        toast.success('Pricing item created successfully');
      }

      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save pricing item'));
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this pricing item?')) {
      return;
    }

    try {
      await pricingAPI.delete(itemId);
      toast.success('Pricing item deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete pricing item');
    }
  };

  const getCategoryBadge = (category) => {
    const colors = {
      consultation: 'bg-purple-100 text-purple-800',
      lab_test: 'bg-cyan-100 text-cyan-800',
      imaging: 'bg-blue-100 text-blue-800',
      procedure: 'bg-violet-100 text-violet-800',
      medicine: 'bg-red-100 text-red-800',
    };
    return colors[category] || 'bg-slate-100 text-slate-800';
  };

  return (
    <div data-testid="pricing-master-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Pricing Master</h1>
            <p className="text-slate-500">Manage service prices and billing rates</p>
          </div>
          {canManage && (
            <Button className="bg-red-500 hover:bg-red-600 rounded-full" onClick={openAddDialog}>
              <Plus className="w-4 h-4 mr-2" />
              Add Pricing Item
            </Button>
          )}
        </div>
      </header>

      <div className="page-content">
        <Tabs defaultValue="prices" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="prices">Price List</TabsTrigger>
            <TabsTrigger value="history">Change History</TabsTrigger>
          </TabsList>

          {/* Price List Tab */}
          <TabsContent value="prices">
            <Card>
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-red-500" />
                    Service Prices
                  </CardTitle>
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={5}>
                      <SelectItem value="all">All Categories</SelectItem>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center">Loading...</div>
                ) : items.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">No pricing items found</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Category</TableHead>
                        <TableHead>Item Code</TableHead>
                        <TableHead>Item Name</TableHead>
                        <TableHead>Price (₹)</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Updated</TableHead>
                        {canManage && <TableHead className="text-right">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Badge className={getCategoryBadge(item.category)}>
                              {item.category.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{item.item_code}</TableCell>
                          <TableCell className="font-medium">{item.item_name}</TableCell>
                          <TableCell className="font-semibold text-green-600">
                            ₹{item.price.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">{item.description || '-'}</TableCell>
                          <TableCell className="text-xs text-slate-500">
                            {formatDateTime(item.updated_at)}
                          </TableCell>
                          {canManage && (
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(item)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {hasRole(['super_admin', 'admin']) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(item.id)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Change History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader className="border-b">
                <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
                  <History className="w-5 h-5 text-red-500" />
                  Pricing Change History
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {auditLog.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">No changes recorded</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Time</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead>Old Price</TableHead>
                        <TableHead>New Price</TableHead>
                        <TableHead>Changed By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditLog.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            <div className="flex items-center gap-1 text-xs">
                              <Clock className="w-3 h-3" />
                              {formatDateTime(log.timestamp)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              log.action === 'create' ? 'bg-green-100 text-green-800' :
                              log.action === 'update' ? 'bg-blue-100 text-blue-800' :
                              'bg-red-100 text-red-800'
                            }>
                              {log.action}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">{log.item_name}</TableCell>
                          <TableCell>{log.old_price ? `₹${log.old_price}` : '-'}</TableCell>
                          <TableCell className="font-semibold text-green-600">
                            {log.new_price ? `₹${log.new_price}` : '-'}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="text-sm">{log.performed_by_name}</p>
                              <p className="text-xs text-slate-500 capitalize">{log.performed_by_role?.replace('_', ' ')}</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              {editMode ? 'Edit Pricing Item' : 'Add Pricing Item'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Category *</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={5}>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Item Code *</Label>
                <Input
                  value={formData.item_code}
                  onChange={(e) => setFormData({...formData, item_code: e.target.value})}
                  placeholder="e.g., CONS001, LAB001"
                  required
                  disabled={editMode}
                />
              </div>
              <div>
                <Label>Price (₹) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div>
              <Label>Item Name *</Label>
              <Input
                value={formData.item_name}
                onChange={(e) => setFormData({...formData, item_name: e.target.value})}
                placeholder="e.g., General Consultation, CBC Test"
                required
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Additional details about this service"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-red-500 hover:bg-red-600">
                {editMode ? 'Update' : 'Create'} Item
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PricingMaster;
